import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { MeetingProgress } from 'components/meeting/MeetingProgress';
import { DOMESTIC_AREA_CODES, JAPAN_REGIONS, JP_POPULAR_SPOTS, KR_POPULAR_CITIES, OVERSEAS_DESTINATIONS, getOverseasCountries, getDomesticRegions, getCountryName, CountryCode } from 'common/api/tourApi';
import { useSkipIfCreated } from 'hooks/useSkipIfCreated';
import { MapPicker, PickedPlace } from 'components/map/MapPicker';
import { instance } from 'common/axios/axios';
import { getApiUrl } from 'common/axios/getApiUrl';
import { isNativeApp, openNativeGooglePlaceSearch } from 'common/webview';
import { IcGlobe, EMOJI_ICON } from 'components/common/icons/EmojiIcons';
import {
    ActionList, ActionRow, AppScreen, Badge, ChipBar, EmptyState, SearchInput, Section, SectionHeader,
} from 'components/ds';
import styles from './TravelDestination.module.css';

const STORAGE_KEY = 'meeting_create_data';

type TravelType = 'DOMESTIC' | 'OVERSEAS';

interface Destination {
    id: string;
    name: string;
    emoji?: string;
    description?: string;
    imageUrl?: string;
    popular?: boolean;
    areaCode?: string;
    lat?: number;
    lng?: number;
    country?: string;
    /** ISO 2자리 국가코드 (KR/JP/US 등). scope 필터링용. */
    countryCode?: string;
}

// 국내 지역 대표 좌표 (집결장소/숙박지 검색 기준점)
const KR_DOMESTIC_COORDS: Record<string, { lat: number; lng: number }> = {
    seoul:    { lat: 37.5665, lng: 126.9780 },
    incheon:  { lat: 37.4563, lng: 126.7052 },
    daejeon:  { lat: 36.3504, lng: 127.3845 },
    daegu:    { lat: 35.8714, lng: 128.6014 },
    gwangju:  { lat: 35.1595, lng: 126.8526 },
    busan:    { lat: 35.1796, lng: 129.0756 },
    ulsan:    { lat: 35.5384, lng: 129.3114 },
    sejong:   { lat: 36.4800, lng: 127.2890 },
    gyeonggi: { lat: 37.4138, lng: 127.5183 },
    gangwon:  { lat: 37.8228, lng: 128.1555 },
    chungbuk: { lat: 36.6357, lng: 127.4917 },
    chungnam: { lat: 36.5184, lng: 126.8000 },
    gyeongbuk:{ lat: 36.5760, lng: 128.5056 },
    gyeongnam:{ lat: 35.4606, lng: 128.2132 },
    jeonbuk:  { lat: 35.7175, lng: 127.1530 },
    jeonnam:  { lat: 34.8161, lng: 126.4629 },
    jeju:     { lat: 33.4996, lng: 126.5312 },
};

// 국내 지역 (현재 국가 기준으로 동적 생성)
// 광역시도 + 인기 세부 관광지 모두 표시 — 사용자가 원하는 곳을 한눈에 찾기 쉽게.
const buildDomesticRegions = (countryCd: CountryCode): Destination[] => {
    // 자체 호스팅 이미지 경로 — public/images/destinations/{id}.jpg
    // 외부 도메인 변경/차단에 영향 없음. 빌드 산출물에 포함.
    // (실제 이미지 다운로드: scripts/download-destination-images.mjs)
    const localImage = (id: string) => `/images/destinations/${id}.jpg`;

    if (countryCd === 'JP') {
        const regions = Object.entries(JAPAN_REGIONS).map(([id, data]) => ({
            id, name: data.name, emoji: data.emoji,
            description: data.description, lat: data.lat, lng: data.lng,
            popular: ['jp-tokyo', 'jp-osaka', 'jp-kyoto', 'jp-fukuoka', 'jp-sapporo', 'jp-okinawa'].includes(id),
            imageUrl: localImage(id),
        }));
        const spots = Object.entries(JP_POPULAR_SPOTS).map(([id, data]) => ({
            id, name: data.name, emoji: data.emoji,
            description: data.description, lat: data.lat, lng: data.lng,
            popular: false,
            imageUrl: localImage(id),
        }));
        return [...regions, ...spots];
    }

    // 기본: 한국
    const krDescs: Record<string, string> = {
        seoul: '경복궁, 명동, 한강', busan: '해운대, 광안리', jeju: '한라산, 올레길',
        gangwon: '설악산, 속초, 양양', gyeonggi: '수원화성, 에버랜드', incheon: '월미도, 송도',
        daejeon: '유성온천, 엑스포', daegu: '동성로, 팔공산', gwangju: '무등산, 양림동',
        ulsan: '대왕암, 간절곶', sejong: '세종호수공원', chungbuk: '수안보, 단양',
        chungnam: '공주, 부여', gyeongbuk: '경주, 안동', gyeongnam: '통영, 거제',
        jeonbuk: '전주 한옥마을', jeonnam: '여수, 순천만',
    };
    const provinces = Object.entries(DOMESTIC_AREA_CODES).map(([id, data]) => ({
        id, name: data.name, emoji: data.emoji, areaCode: data.code,
        popular: ['seoul', 'busan', 'jeju', 'gangwon', 'gyeonggi', 'jeonbuk'].includes(id),
        description: krDescs[id] || '',
        imageUrl: localImage(id),
        lat: KR_DOMESTIC_COORDS[id]?.lat,
        lng: KR_DOMESTIC_COORDS[id]?.lng,
    }));
    const cities = Object.entries(KR_POPULAR_CITIES).map(([id, data]) => ({
        id, name: data.name, emoji: data.emoji,
        description: data.description, lat: data.lat, lng: data.lng,
        popular: false,
        imageUrl: localImage(id),
    }));
    return [...provinces, ...cities];
};

/* 해외가 준비중인 동안에는 그리지 않습니다. 준비중을 풀 때 되살릴 자리라 지우지 않습니다. */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const OVERSEAS_COUNTRIES = getOverseasCountries();

/**
 * 자체 호스팅 이미지 카드 썸네일.
 * 이미지 로드 실패 시 emoji 로 fallback — 404 / 외부 차단 / 빌드 누락 등 모든 케이스 대응.
 */
const DestThumb: React.FC<{ url?: string; emoji?: string; alt: string }> = ({ url, emoji, alt }) => {
    const [failed, setFailed] = useState(false);
    if (!url || failed) {
        const Ic = (emoji && EMOJI_ICON[emoji]) || IcGlobe;
        return <span className={styles.thumb}><Ic size={28} /></span>;
    }
    return (
        <span className={styles.thumb}>
            <img src={url} alt={alt} loading="lazy" onError={() => setFailed(true)} />
        </span>
    );
};

export const TravelDestination = () => {
    useSkipIfCreated();
    const navigate = useNavigate();
    const { t } = useTranslation();
    // 현재 국가 감지 (네이티브 device 정보 또는 기본 KR)
    const userCountry: CountryCode = window.__DEVICE__?.countryCd || 'KR';
    const countryName = getCountryName(userCountry);

    // 국내 지역 데이터 (현재 국가 기반)
    const DOMESTIC_REGIONS = useMemo(() => buildDomesticRegions(userCountry), [userCountry]);

    // 해외: 현재 국가를 제외한 나라들
    const overseasDestinations = useMemo(() =>
        OVERSEAS_DESTINATIONS.filter(d => {
            if (userCountry === 'JP') return d.countryEn !== 'Japan';
            if (userCountry === 'KR') return true; // 한국은 전부 해외
            return true;
        }), [userCountry]);

    const loadSavedData = () => {
        const saved = sessionStorage.getItem(STORAGE_KEY);
        return saved ? JSON.parse(saved) : {};
    };
    const savedData = loadSavedData();

    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
    const [scope, setScope] = useState<TravelType>('DOMESTIC');
    const [apiSearchResults, setApiSearchResults] = useState<Destination[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showMapPicker, setShowMapPicker] = useState(false);
    const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // MapPicker 에서 좌표/주소 확정 시 — 사용자 국가코드 vs 핀 국가코드 비교로 국내/해외 자동 판정
    const handleMapPicked = (picked: PickedPlace) => {
        const isDomestic = picked.countryCode === userCountry;
        const dest: Destination = {
            id: `map-${picked.lat.toFixed(5)},${picked.lng.toFixed(5)}`,
            name: picked.name || picked.address.split(',')[0]?.trim() || `${picked.lat.toFixed(4)},${picked.lng.toFixed(4)}`,
            description: picked.address,
            lat: picked.lat,
            lng: picked.lng,
            country: isDomestic ? undefined : (picked.address.split(',').pop()?.trim() || picked.countryCode),
        };
        setShowMapPicker(false);
        handleSelect(dest, isDomestic ? 'DOMESTIC' : 'OVERSEAS');
    };

    // 지도 진입 — 해외 토글 + 네이티브 환경이면 Google Maps 화면으로 위임 (오토컴플리트/POI 정확도 ↑).
    // 그 외(국내 또는 웹) 는 자체 Leaflet 모달.
    const handlePickFromMap = () => {
        if (scope === 'OVERSEAS' && isNativeApp()) {
            openNativeGooglePlaceSearch({
                placeholder: t('travelDest.searchPlaceholder'),
                confirmButtonText: t('travelDest.pickThisLocation'),
                title: t('travelDest.title'),
            }).then(place => {
                if (!place || !place.lat || !place.lng) return;
                const country = (place.address || '').split(',').pop()?.trim();
                const dest: Destination = {
                    id: `gp-${(place as any).placeId || `${place.lat},${place.lng}`}`,
                    name: place.name || (place.address || '').split(',')[0]?.trim() || `${place.lat.toFixed(4)},${place.lng.toFixed(4)}`,
                    description: place.address,
                    lat: place.lat,
                    lng: place.lng,
                    country,
                };
                handleSelect(dest, 'OVERSEAS');
            });
            return;
        }
        setShowMapPicker(true);
    };

    // 백엔드 통합 검색 endpoint 호출 (Nominatim wrapper, scope/userCountry 서버측 필터)
    // 디바운스 500ms — Nominatim Fair Use 정책 준수 + 백엔드 throttle.
    useEffect(() => {
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        if (!searchQuery.trim() || searchQuery.trim().length < 2) {
            setApiSearchResults([]);
            return;
        }
        searchTimerRef.current = setTimeout(async () => {
            setIsSearching(true);
            try {
                const lang = navigator.language?.split('-')[0] || 'ko';
                const res = await instance.get(getApiUrl('travelSearch'), {
                    params: {
                        q: searchQuery,
                        scope: scope === 'DOMESTIC' ? 'domestic' : 'overseas',
                        userCountry,
                        acceptLanguage: `${lang},ko,en,ja`,
                    },
                });
                const items = res?.data?.data || [];
                const mapped: Destination[] = (items || [])
                    .filter((r: any) => r.lat && r.lng)
                    .map((r: any) => ({
                        id: r.id || `nom-${r.lat},${r.lng}`,
                        name: r.name || (r.address || '').split(',')[0]?.trim() || '',
                        description: r.address || '',
                        country: r.country,
                        countryCode: r.countryCode,
                        lat: Number(r.lat),
                        lng: Number(r.lng),
                    }));
                setApiSearchResults(mapped);
            } catch {
                setApiSearchResults([]);
            }
            setIsSearching(false);
        }, 500);
        return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
    }, [searchQuery, scope, userCountry]);

    // 통합 검색 (로컬 데이터 + API 결과) — 토글(scope) 에 따라 자국/해외만 표시
    const searchResults = useMemo(() => {
        if (!searchQuery.trim()) return null;
        const q = searchQuery.toLowerCase();
        const domestic = DOMESTIC_REGIONS.filter(r =>
            r.name?.includes(q) || r.description?.includes(q)
        ).map(r => ({ ...r, type: 'DOMESTIC' as TravelType }));

        const overseas = overseasDestinations
            .filter(d => d.name.includes(q) || d.country.includes(q) || d.countryEn.toLowerCase().includes(q) || d.description.includes(q))
            .map(d => ({ id: d.id, name: d.name, emoji: d.emoji, description: d.description, country: d.country, lat: d.lat, lng: d.lng, type: 'OVERSEAS' as TravelType }));

        // API 결과 — countryCode 기반으로 DOMESTIC/OVERSEAS 자동 분류
        const localNames = new Set([...domestic.map(d => d.name), ...overseas.map(d => d.name)]);
        const apiResults = apiSearchResults
            .filter(r => !localNames.has(r.name))
            .map(r => {
                const isDomestic = r.countryCode === userCountry;
                return {
                    ...r,
                    type: (isDomestic ? 'DOMESTIC' : 'OVERSEAS') as TravelType,
                    emoji: isDomestic ? '🏠' : '🌍',
                };
            });

        const all = [...domestic, ...overseas, ...apiResults];
        // scope 토글 적용 — 사용자가 보고 싶다고 선택한 쪽만 노출
        return all.filter(item => item.type === scope);
    }, [searchQuery, DOMESTIC_REGIONS, overseasDestinations, apiSearchResults, userCountry, scope]);

    // 도시 선택
    const overseasCities = useMemo(() => {
        if (!selectedCountry) return [];
        return overseasDestinations.filter(d => d.country === selectedCountry).map(d => ({
            id: d.id, name: d.name, emoji: d.emoji, description: d.description,
            country: d.country, lat: d.lat, lng: d.lng,
        }));
    }, [selectedCountry, overseasDestinations]);

    // 해외 인기 도시 (현재 국가 제외) — 자체 호스팅 이미지 (`/images/destinations/{id}.jpg`)
    /* 해외 준비중 동안 그리지 않습니다 — 되살릴 자리라 지우지 않습니다. */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const overseasPopular = useMemo(() => {
        return overseasDestinations.filter(d => d.popular).map(d => ({
            id: d.id, name: d.name, emoji: d.emoji, description: d.description,
            country: d.country, lat: d.lat, lng: d.lng,
            imageUrl: `/images/destinations/${d.id}.jpg`,
        }));
    }, [overseasDestinations]);

    const handleSelect = (dest: Destination, travelType: TravelType) => {
        /* 해외 여행은 준비중입니다.
           해외로 들어오는 길이 여럿입니다 — 해외 탭의 인기 도시·국가 목록, 지도에서 고르기,
           그리고 국내 탭에서 검색해도 국가코드로 해외가 잡힙니다. travelType 을 정하는 곳이
           여기 하나뿐이라, 이 자리에서 막으면 어느 길로 와도 걸립니다. */
        if (travelType === 'OVERSEAS') {
            /* 검색 결과나 지도에서 들어온 경우에도 준비중 안내가 보이도록
               검색을 접고 해외 탭으로 데려갑니다. 별도 팝업을 띄우지 않습니다. */
            setSearchQuery('');
            setSelectedCountry(null);
            setScope('OVERSEAS');
            return;
        }
        const data = {
            ...savedData,
            travelType,
            selectedRegion: dest,
            destination: dest.country ? `${dest.country} ${dest.name}` : dest.name,
            destinationInfo: {
                name: dest.name, type: travelType, regionId: dest.id,
                areaCode: dest.areaCode, lat: dest.lat, lng: dest.lng, country: dest.country,
            }
        };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));

        // 수정 모드 — 모임 수정 화면으로 돌아감 (sessionStorage 의 destination 을 MeetingEdit 가 read)
        const editReturnTo = (savedData as any)?.returnTo;
        if ((savedData as any)?.isEditMode && editReturnTo) {
            navigate(editReturnTo, { replace: true });
            return;
        }

        navigate('/meeting/create/travel-step4');
    };

    return (
        <AppScreen title={t('travelDest.title')} bar="sub">
            <MeetingProgress currentStep={3} isTravel />

            <Section spacing="tight">
                <SearchInput
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder={t('travelDest.searchPlaceholder')}
                    clearLabel={t('travelDest.close')}
                />
            </Section>

            {searchResults ? (
                /* 검색 결과 */
                <Section divided last>
                    <SectionHeader
                        title={t('travelDest.searchResults', { count: searchResults.length })}
                    />
                    {searchResults.length === 0 ? (
                        <p className={styles.state}>
                            {isSearching ? t('travelDest.searching') : t('travelDest.noResults')}
                        </p>
                    ) : (
                        <ul className={styles.list}>
                            {searchResults.map(r => (
                                <li key={r.id}>
                                    <button
                                        type="button"
                                        className={styles.row}
                                        onClick={() => handleSelect(r, r.type)}
                                    >
                                        <span className={styles.rowBody}>
                                            <span className={styles.rowTitle}>
                                                {r.country ? `${r.name} (${r.country})` : r.name}
                                            </span>
                                            <span className={styles.rowMeta}>{r.description}</span>
                                        </span>
                                        <Badge tone="outline">
                                            {r.type === 'DOMESTIC' ? t('travelDest.domestic') : t('travelDest.overseas')}
                                        </Badge>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </Section>
            ) : selectedCountry ? (
                /* 한 나라 안 도시 고르기 */
                <Section divided last>
                    <SectionHeader
                        title={t('travelDest.citiesOf', { country: selectedCountry })}
                        count={overseasCities.length}
                        moreLabel={t('common.back')}
                        onMore={() => setSelectedCountry(null)}
                    />
                    <div className={styles.grid}>
                        {overseasCities.map(city => (
                            <button
                                key={city.id}
                                type="button"
                                className={styles.card}
                                onClick={() => handleSelect(city, 'OVERSEAS')}
                            >
                                <DestThumb url={(city as any).imageUrl} emoji={city.emoji} alt={city.name} />
                                <span className={styles.cardName}>{city.name}</span>
                                {city.description && <span className={styles.cardDesc}>{city.description}</span>}
                            </button>
                        ))}
                    </div>
                </Section>
            ) : (
                <>
                    {/* 국내 · 해외 */}
                    <Section spacing="tight">
                        <ChipBar
                            options={[
                                { value: 'DOMESTIC', label: t('travelDest.domestic') },
                                { value: 'OVERSEAS', label: t('travelDest.overseas') },
                            ]}
                            value={scope}
                            onChange={(v) => setScope(v as typeof scope)}
                            ariaLabel={t('travelDest.title')}
                        />
                    </Section>

                    {scope === 'DOMESTIC' ? (
                        <Section divided>
                            <SectionHeader
                                title={t('travelDest.popularDomestic')}
                                sub={countryName}
                            />
                            <div className={styles.grid}>
                                {DOMESTIC_REGIONS.map(dest => (
                                    <button
                                        key={dest.id}
                                        type="button"
                                        className={styles.card}
                                        onClick={() => handleSelect(dest, 'DOMESTIC')}
                                    >
                                        <DestThumb url={dest.imageUrl} emoji={dest.emoji} alt={dest.name} />
                                        <span className={styles.cardName}>{dest.name}</span>
                                        {dest.description && <span className={styles.cardDesc}>{dest.description}</span>}
                                    </button>
                                ))}
                            </div>
                        </Section>
                    ) : (
                        /* 해외 여행 준비중 — 도시·국가 목록을 그리지 않습니다.
                           탭 자체를 없애면 "되던 게 사라졌다" 로 보여서, 탭은 남기고
                           안에서 상태를 알립니다. */
                        <Section divided>
                            <EmptyState
                                icon={<IcGlobe size={28} />}
                                title={t('travelDest.overseasPreparingTitle')}
                                body={t('travelDest.overseasPreparingDesc')}
                            />
                        </Section>
                    )}

                    {/* 지도에서 직접 고르기 — 해외 탭에서는 숨깁니다.
                        고른 자리가 해외면 게이트에 걸려 준비중 안내로 되돌아오므로,
                        눌러 봐야 헛걸음입니다. */}
                    {scope === 'DOMESTIC' && (
                        <Section divided last>
                            <ActionList>
                                <ActionRow
                                    title={t('travelDest.pickFromMap')}
                                    desc={t('travelDest.pickFromMapDesc')}
                                    onClick={handlePickFromMap}
                                />
                            </ActionList>
                        </Section>
                    )}
                </>
            )}

            {showMapPicker && (
                <MapPicker
                    title={t('travelDest.pickFromMap')}
                    loadingLabel={t('travelDest.loading')}
                    confirmLabel={t('travelDest.pickThisLocation')}
                    closeLabel={t('travelDest.close')}
                    onClose={() => setShowMapPicker(false)}
                    onConfirm={handleMapPicked}
                />
            )}
        </AppScreen>
    );
};
